import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { query } from "./api.js";
import { agentFor, initSession, revokeSession, startSignIn } from "./atproto.js";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [booting, setBooting] = useState(true);
  const [error, setError] = useState("");

  const refreshProfile = useCallback(async did => {
    const target = did || session?.sub;
    if (!target) return null;
    try {
      const response = await query(`/v1/profiles/${encodeURIComponent(target)}`, { viewerDid: target });
      setProfile(response.profile);
      return response.profile;
    } catch (reason) {
      if (reason.status === 404) {
        const pending = { did: target, handle: target, displayName: "", onboardingCompleted: false, counts: {} };
        setProfile(pending);
        return pending;
      }
      throw reason;
    }
  }, [session?.sub]);

  useEffect(() => {
    let active = true;
    initSession().then(async restored => {
      if (!active) return;
      setSession(restored);
      if (restored) await refreshProfile(restored.sub);
    }).catch(reason => {
      if (active) setError(reason.message || "No se ha podido recuperar la sesión.");
    }).finally(() => active && setBooting(false));
    return () => { active = false; };
  }, []);

  const signIn = useCallback(async identifier => {
    setError("");
    let timeout;
    try {
      await Promise.race([
        startSignIn(identifier.trim()),
        new Promise((_, reject) => {
          timeout = window.setTimeout(() => reject(new Error("El proveedor está tardando demasiado en responder.")), 12_000);
        })
      ]);
    } catch (reason) {
      const message = reason?.message || "No se ha podido abrir tu proveedor de acceso.";
      setError(message.includes("resolve") || message.includes("handle")
        ? "No encontramos ese usuario de Bluesky. Revisa el nombre y vuelve a intentarlo."
        : `${message} Vuelve a intentarlo en unos segundos.`);
      throw reason;
    } finally {
      window.clearTimeout(timeout);
    }
  }, []);

  const logout = useCallback(async () => {
    const did = session?.sub;
    setSession(null);
    setProfile(null);
    if (did) await revokeSession(did).catch(() => {});
  }, [session?.sub]);

  const agent = useMemo(() => session ? agentFor(session) : null, [session]);
  const value = useMemo(() => ({
    session, agent, profile, setProfile, booting, error, signIn, logout, refreshProfile
  }), [session, agent, profile, booting, error, signIn, logout, refreshProfile]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth debe usarse dentro de AuthProvider");
  return value;
}
